import { distributionAudienceSchema, distributionDeliveryModeSchema, distributionRecipientKindSchema, isValidTimeZone, } from '@teo/types';
import { z } from 'zod';
import { displayId } from './common.js';
export const listDistributionRecipientsSchema = z.object({
    ownershipId: displayId,
});
export const recipientSendOutputSchema = z.object({
    personKey: z.string(),
    kind: distributionRecipientKindSchema,
    name: z.string().nullable(),
    address: z.string().nullable(),
    unitIdentifier: z.string().nullable(),
    addressLines: z.array(z.string()),
});
export const recipientRowOutputSchema = z.object({
    kind: distributionRecipientKindSchema,
    id: displayId,
    unitDisplayId: displayId.nullable(),
    unitIdentifier: z.string().nullable(),
    recipientName: z.string().nullable(),
    boardMemberNames: z.array(z.string()),
    sends: z.array(recipientSendOutputSchema),
});
export const listDistributionRecipientsOutputSchema = z.object({
    ownershipName: z.string(),
    ownershipEmail: z.string(),
    rows: z.array(recipientRowOutputSchema),
    boardRows: z.array(recipientRowOutputSchema),
});
export const MAX_SIGNATURE_LENGTH = 1000;
export const MAX_BCC_OTHER_ADDRESSES = 5;
export const createDocumentDistributionDraftSchema = z.object({
    ownershipId: displayId,
    subject: z.string().trim().min(1).max(255),
    body: z.string().trim().min(1),
    signature: z.string().trim().max(MAX_SIGNATURE_LENGTH),
    documentIds: z.array(displayId),
    bccOwnershipEmail: z.boolean().optional(),
    bccOtherAddresses: z.array(z.string().trim().email()).max(MAX_BCC_OTHER_ADDRESSES).optional(),
    sourceModule: z.string().trim().min(1).max(100),
    idempotencyKey: z.string().trim().min(1).max(100),
});
export const documentDistributionStatusSchema = z.enum([
    'draft',
    'sending',
    'completed',
    'completed_with_failures',
]);
export const documentDistributionOutputSchema = z.object({
    distributionId: displayId,
    status: documentDistributionStatusSchema,
    subject: z.string(),
    createdAt: z.date(),
    sentAt: z.date().nullable(),
});
export const sendDocumentDistributionSchema = z.object({
    distributionId: displayId,
    ownershipId: displayId,
    audience: distributionAudienceSchema,
    deliveryMode: distributionDeliveryModeSchema,
    unitDisplayIds: z.array(displayId),
    renterDisplayIds: z.array(displayId),
    boardRosterMemberDisplayIds: z.array(displayId),
    timeZone: z.string().refine(isValidTimeZone, { message: 'Unknown time zone' }),
});
export const distributionRecipientStatusSchema = z.enum([
    'pending',
    'sending',
    'sent',
    'skipped_no_email',
    'failed',
]);
export const distributionRecipientResultOutputSchema = z.object({
    recipientKind: distributionRecipientKindSchema,
    recipientName: z.string().nullable(),
    unitIdentifier: z.string().nullable(),
    resolvedAddress: z.string().nullable(),
    status: distributionRecipientStatusSchema,
    errorMessage: z.string().nullable(),
});
export const sendDocumentDistributionOutputSchema = z.object({
    distributionId: displayId,
    status: z.literal('sending'),
});
export const getDocumentDistributionSchema = z.object({
    ownershipId: displayId,
    distributionId: displayId,
});
export const resendFailedDistributionSchema = getDocumentDistributionSchema;
export const distributionRecordOutputSchema = z
    .object({ documentId: displayId, filename: z.string() })
    .nullable();
export const sentEmailOutputSchema = z.object({
    audience: distributionAudienceSchema,
    deliveryMode: distributionDeliveryModeSchema,
    sentByName: z.string(),
    attachmentNames: z.array(z.string()),
    bccAddresses: z.array(z.string()),
    letter: z
        .object({
        recipientName: z.string().nullable(),
        recipientAddress: z.string().nullable(),
        text: z.string(),
    })
        .nullable(),
});
export const getDocumentDistributionOutputSchema = documentDistributionOutputSchema.extend({
    email: sentEmailOutputSchema,
    recipients: z.array(distributionRecipientResultOutputSchema),
    record: distributionRecordOutputSchema,
});
export const saveDistributionRecordSchema = getDocumentDistributionSchema;
export const saveDistributionRecordOutputSchema = z.object({
    record: distributionRecordOutputSchema,
});
export const listDocumentDistributionsSchema = z.object({
    ownershipId: displayId,
    limit: z
        .number()
        .int()
        .min(1)
        .max(100)
        .default(20)
        .describe('How many sends to return, from 1 to 100. The default is 20.'),
    cursor: z
        .number()
        .int()
        .min(0)
        .default(0)
        .describe('How many of the newest sends to skip, for the next page. Start at 0.'),
});
export const distributionHistoryItemOutputSchema = documentDistributionOutputSchema.extend({
    audience: distributionAudienceSchema,
    deliveryMode: distributionDeliveryModeSchema,
    sentByName: z.string(),
    counts: z.object({
        total: z.number().int().nonnegative(),
        sent: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    }),
    record: distributionRecordOutputSchema,
});
export const listDocumentDistributionsOutputSchema = z.object({
    items: z.array(distributionHistoryItemOutputSchema),
    nextCursor: z.number().int().min(0).nullable(),
});
export const getDistributionSignatureSchema = z.object({
    ownershipId: displayId,
});
export const distributionSignatureOutputSchema = z.object({
    signature: z.string().nullable(),
});
export const saveDistributionSignatureSchema = z.object({
    ownershipId: displayId,
    signature: z.string().trim().max(MAX_SIGNATURE_LENGTH),
});
//# sourceMappingURL=documentDistributions.js.map